import CloseIcon from '../../../assets/icons/close.svg?react';
import { useState, useEffect } from 'react';
import { useUpdateNotificationRule } from '../../../hooks/queries/logQueries';
import Selector from '../../../components/Selector/Selector';
import TargetIcon from '../../../assets/icons/target.svg?react';
import AlertSetUpTargetEditModal from './AlertSetUpTargetEditModal';
import { useAuthContext } from '../../../contexts/AuthContext';
const AlertSetUpEditSideModal = ({ target, onClose, onSuccess }) => {
  const { user } = useAuthContext();
  
  const conditionTypeOptions = [
    { id: 'LogLevel', label: 'LogLevel' },
    { id: 'Message', label: 'Message' },
    { id: 'ServiceName', label: 'ServiceName' },
    { id: 'ErrorCode', label: 'ErrorCode' },
  ];

  // 조건타입에 따른 연산자 옵션
  const getOperatorOptions = (conditionType) => {
    if (!conditionType) return [];
    
    switch (conditionType.id) {
      case 'LogLevel':
        return [
          { id: 'EQUAL', label: 'Equal' },
          { id: 'ALL', label: 'All' },
        ];
      case 'ServiceName':
      case 'ErrorCode':
        return [
          { id: 'EQUAL', label: 'Equal' },
          { id: 'NOT_EQUAL', label: 'Not Equal' },
          { id: 'CONTAINS', label: 'Contains' },
        ];
      case 'Message':
        return [
          { id: 'CONTAINS', label: 'Contains' },
        ];
      default:
        return [];
    }
  };

  const updateNotificationRule = useUpdateNotificationRule();
  const [isPending, setIsPending] = useState(false);
  const [isNotificationTargetsModalOpen, setIsNotificationTargetsModalOpen] = useState(false);
  const [selectedConditionType, setSelectedConditionType] = useState(null);
  const [selectedConditionOperator, setSelectedConditionOperator] = useState(null);
  const [selectedConditionValue, setSelectedConditionValue] = useState(null);
  const [formData, setFormData] = useState({
    ruleName: '',
    conditionType: '',
    conditionOperator: '',
    conditionValue: '',
    isActive: true,
    userSysId: user.UserSysId,
    notificationTargets: []
  });

  // LogLevel일 때 사용할 조건값 옵션
  const logLevelValueOptions = [
    { id: 'INFO', label: 'INFO' },
    { id: 'WARNING', label: 'WARNING' },
    { id: 'ERROR', label: 'ERROR' },
    { id: 'CRITICAL', label: 'CRITICAL' },
    { id: 'ALL', label: 'All' },
  ];

  const operatorOptions = getOperatorOptions(selectedConditionType);

  // 조건타입 변경 시 상태 및 formData 동기화
  const handleConditionTypeChange = (value) => {
    // 사용자 선택으로 타입 변경 시 state 및 formData 동기화
    setSelectedConditionType(value);
    setSelectedConditionOperator(null);
    setSelectedConditionValue(null);
    setFormData(prev => ({
      ...prev,
      conditionType: value?.id || '',
      conditionOperator: '',
      conditionValue: ''
    }));
  };
  
  useEffect(() => {
    if(selectedConditionOperator){
      setFormData(prev => ({ ...prev, conditionOperator: selectedConditionOperator.id }));
    }
  }, [selectedConditionOperator]);
  
  // LogLevel일 때 조건값 선택
  useEffect(() => {
    if(selectedConditionValue){
      setFormData(prev => ({ ...prev, conditionValue: selectedConditionValue.id }));
    }
  }, [selectedConditionValue]);
  const handleFormDataChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  const handleSubmitNotificationTargets = (targets) => {
    setFormData({ ...formData, notificationTargets: targets });
    setIsNotificationTargetsModalOpen(false);
  }

  // target이 변경될 때마다 모든 state를 초기화
  useEffect(() => {
    if (target) {
      // formData 초기화
      setFormData({
        ruleName: target.RuleName || '',
        conditionType: target.ConditionType || '',
        conditionOperator: target.ConditionOperator || '',
        conditionValue: target.ConditionValue || '',
        isActive: target.IsActive ?? true,
        userSysId: user.UserSysId,
        notificationTargets: target.NotificationTargets?.map(t => t.NotificationTargetSysId) || []
      });

      // 조건타입 설정
      if (target.ConditionType) {
        const conditionType = conditionTypeOptions.find(option => option.id === target.ConditionType);
        setSelectedConditionType(conditionType);
        
        // 조건타입에 맞는 연산자 옵션에서 현재 연산자 찾기
        const availableOperators = getOperatorOptions(conditionType);
        const operator = availableOperators.find(option => option.id === target.ConditionOperator);
        setSelectedConditionOperator(operator);
        
        // LogLevel일 때 조건값도 설정
        if (target.ConditionType === 'LogLevel') {
          const value = logLevelValueOptions.find(option => option.id === target.ConditionValue);
          setSelectedConditionValue(value);
        } else {
          setSelectedConditionValue(null);
        }
      } else {
        setSelectedConditionType(null);
        setSelectedConditionOperator(null);
        setSelectedConditionValue(null);
      }
    }
  }, [target?.NotificationRuleSysId]); // target의 ID가 변경될 때마다 초기화
  const handleUpdateNotificationRule = async () => {
    try {
      setIsPending(true);
      await updateNotificationRule.mutateAsync({
        ruleSysId: target.NotificationRuleSysId,
        payload: formData,
      });
      onSuccess();
    } catch (error) {
      console.error('알림 설정 수정 실패:', error);
      alert('알림 설정 수정에 실패했습니다.');
    }
    finally {
      setIsPending(false);
    }
  }


  const transformNotificationTargetData = (data) => {
    if (!data?.Items) return [];
    return data.Items.map((item) => ({
      id: item.NotificationTargetSysId,
      label: item.TargetValue,
      data: item,
    }));
  };
  const buildApiParams = (searchQuery) => {
    return {
      targetValue: searchQuery,
    };
  };
  const getQueryKeys = (searchQuery) => {
    return ['targetValue', 'selector', searchQuery];
  };

  
  return (
    <div className="side-modal-background">
      <div className="side-modal-container">
        <div className="side-modal-header">
          <h3>알림 설정 수정</h3>
          <button className="side-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="side-modal-content">
          <table className='transparent-table w-full'>
            <tr>
              <td>
                <label htmlFor="ruleName" className='w-20 mr-10 text-b2-700 required'>규칙명</label>
              </td>
              <td>
                <input type="text" id="ruleName" name="ruleName" className='w-full input-primary' placeholder='알림명을 입력해주세요.' value={formData.ruleName} onChange={handleFormDataChange} />
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="conditionType" className='w-20 mr-10 text-b2-700 required'>조건타입</label>
              </td>
              <td>
                <Selector 
                  id="conditionType" 
                  options={conditionTypeOptions} 
                  placeholder="조건타입을 선택해주세요." 
                  mode="local" 
                  value={selectedConditionType} 
                  onChange={handleConditionTypeChange} 
                  enableSearch={false} 
                />
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="conditionOperator" className='w-20 mr-10 text-b2-700 required'>조건연산자</label>
              </td>
              <td>
                <Selector id="conditionOperator" options={operatorOptions} placeholder="조건연산자를 선택해주세요." mode="local" value={selectedConditionOperator} onChange={setSelectedConditionOperator} enableSearch={false} />
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="conditionValue" className='w-20 mr-10 text-b2-700 required'>조건값</label>
              </td>
              <td>
                {selectedConditionType?.id === 'LogLevel' ? (
                  <Selector 
                    id="conditionValue" 
                    options={logLevelValueOptions} 
                    placeholder="조건값을 선택해주세요." 
                    mode="local" 
                    value={selectedConditionValue} 
                    onChange={setSelectedConditionValue} 
                    enableSearch={false} 
                  />
                ) : (
                  <input 
                    type="text" 
                    id="conditionValue" 
                    name="conditionValue" 
                    className='w-full input-primary' 
                    placeholder='조건값을 입력해주세요.' 
                    value={formData.conditionValue || ''} 
                    onChange={handleFormDataChange} 
                  />
                )}
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="isActive" className='w-20 mr-10 text-b2-700 required'>활성여부</label>
              </td>
              <td>
                <div className='flex align-center gap-2'>
                  <input type="checkbox" id="isActive" name="isActive" checked={formData.isActive} onChange={() => setFormData({ ...formData, isActive: !formData.isActive })} />
                  <label htmlFor="isActive" className='text-b2-400 text-gray-600'>활성</label>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <label htmlFor="notificationTargets" className='w-20 mr-10 text-b2-700 required'>알림대상</label>
              </td>
              <td>
                <div className='flex align-center justify-between'>
                  <div className='flex align-center gap-2'>
                    <TargetIcon color='var(--gray-600)' width={20} height={20} />  
                    <p className='text-b2-400 text-gray-600'>{formData.notificationTargets.length}</p>
                  </div>
                  <button className='btn small-btn btn-style-5' onClick={() => setIsNotificationTargetsModalOpen(true)}>
                    대상 설정
                  </button>
                </div>
              </td>
            </tr>
          </table>
          <div className="mt-10">
            <button className="btn btn-primary big-btn w-full" onClick={handleUpdateNotificationRule} disabled={isPending || !formData.ruleName || !formData.conditionType || !formData.conditionOperator || !formData.conditionValue || !formData.notificationTargets}>{isPending ? '수정중...' : '수정'}</button>
          </div>
        </div>
      </div>
      {isNotificationTargetsModalOpen && (
        <AlertSetUpTargetEditModal onClose={() => setIsNotificationTargetsModalOpen(false)} target={formData.notificationTargets} onSubmit={handleSubmitNotificationTargets} />
      )}
    </div>
  )
}

export default AlertSetUpEditSideModal